/**
 * The shape of a piece of text, kept apart from the words in it.
 *
 * Two separate complaints turn out to be one missing idea:
 *
 *   • Pressing Enter to put a blank line between two paragraphs bought a whole
 *     new model call. Nothing about the QUESTION changed — the same sentences in
 *     the same order — so nothing about the answer would have changed either,
 *     but the cache is keyed on the text and the text now had one more `\n` in
 *     it. Every tap of Enter in a long paste was a fresh translation of the
 *     entire paste.
 *
 *   • The answer came back in whatever shape the model felt like. Write three
 *     paragraphs separated by blank lines and the translation arrives as one
 *     wall of text, or as three lines with the blanks removed. The layout
 *     someone typed on purpose is part of what they wrote.
 *
 * Both are answered by splitting text into its SEGMENTS (the lines that have
 * words in them) and its SHAPE (the blank lines, the indents, the leading and
 * trailing whitespace):
 *
 *   • the segments are what gets asked, cached and paid for — so any edit that
 *     only moves whitespace lands on the answer already in hand, free;
 *   • the shape is re-applied to the answer on the way to the screen — so the
 *     output is laid out like the input, without the model having to be trusted
 *     to do it, and the moment the shape changes the answer re-lays out with it.
 *
 * The same segment split is what lets the panes point at each other: segment 3
 * of the answer is the translation of segment 3 of the source, which is all
 * `align` below needs to know.
 *
 * Nothing here is language-specific and nothing here is lossy for the reader:
 * `restore(translate(canonical(x)))` puts the layout back exactly as typed.
 */

export interface Segment {
  /** The line's words, with the indent and any trailing spaces taken off. */
  text: string;
  /** Where it sits in the ORIGINAL string — what the highlight overlay needs. */
  start: number;
  end: number;
}

export interface TextShape {
  segments: Segment[];
  /** The indent of each segment, so a bullet list comes back as a bullet list. */
  indents: string[];
  /**
   * What sits BEFORE each segment: `gaps[i]` precedes `segments[i]`, so
   * `gaps[0]` is the leading whitespace of the whole text and every other entry
   * is the run of newlines that separates one segment from the last.
   */
  gaps: string[];
  /** Whatever followed the final segment. */
  tail: string;
}

/**
 * Split a string into what it says and how it is laid out.
 *
 * Offsets are into the string exactly as passed in — NOT into a normalised copy
 * of it. The highlight overlay in the source pane slices the live textarea value
 * with these, and NFC normalisation can CHANGE THE LENGTH of Korean text
 * (three decomposed jamo become one syllable), so offsets taken from a
 * normalised string would slide out of position by one character per syllable
 * against the box they are supposed to be marking up. Normalisation belongs to
 * `canonical`, where the result is hashed rather than measured.
 *
 * `\r\n` is handled here regardless, because a Windows paste must not produce a
 * phantom empty segment between every pair of lines.
 */
export function shapeOf(raw: string): TextShape {
  const src = raw;
  const segments: Segment[] = [];
  const indents: string[] = [];
  const gaps: string[] = [];

  let pendingGap = '';
  let at = 0;

  for (const line of src.split('\n')) {
    const isLast = at + line.length >= src.length;
    const body = line.trim();
    if (!body) {
      // A blank line is shape, not content. It joins the gap in front of
      // whatever comes next.
      pendingGap += line + (isLast ? '' : '\n');
    } else {
      const indent = line.slice(0, line.length - line.trimStart().length);
      gaps.push(pendingGap);
      pendingGap = isLast ? '' : '\n';
      indents.push(indent);
      const start = at + indent.length;
      segments.push({ text: body, start, end: start + body.length });
    }
    at += line.length + 1; // +1 for the '\n' that `split` consumed
  }

  return { segments, indents, gaps, tail: pendingGap };
}

/**
 * What actually gets asked, cached and billed.
 *
 * One segment per line and nothing else: no blank lines, no indents, no trailing
 * spaces. Two texts with the same words in the same order produce the same
 * string here however they are laid out, which is the whole point — the memo,
 * the server cache and the in-flight de-duplicator all key on this.
 *
 * The single newlines are kept. They are not decoration: they are where one
 * sentence ends and the next begins, and dropping them would hand the model one
 * run-on paragraph and lose the correspondence the panes are matched up on.
 *
 * NFC here and nowhere else, for the reason the server does it (see backend
 * lib/cache.ts): `안` has a precomposed and a decomposed encoding that look
 * identical and compare unequal, and which one arrives depends on the IME and on
 * where the text was copied from. Two spellings of one visible sentence must be
 * one cache key. This string is only ever hashed or sent, never used to index
 * back into the box, so changing its length here costs nothing.
 */
export function canonical(raw: string): string {
  return shapeOf(raw).segments.map((s) => s.text).join('\n').normalize('NFC');
}

/**
 * Where one sentence ends, for the purpose of pointing at it.
 *
 * Two different rules, because a full stop is two different characters doing two
 * different jobs:
 *
 *   • The ASCII `.` `!` `?` are overloaded. They end sentences, and they also
 *     sit inside `$90,000.50`, `v1.2` and `example.com`. So one of these only
 *     ends a sentence when whitespace or the end of the line follows it — which
 *     is exactly the case those three are not.
 *   • The full-width `。！？` are not overloaded: nothing else uses them. And
 *     Chinese, Japanese and Korean routinely run sentences together with no
 *     space at all, so requiring one would find no sentences in the languages
 *     this app is most often translating into.
 *
 * `Mr. Smith` splits, and there is no rule short of a dictionary that stops it.
 * It costs nothing here: a source that splits into four where its translation
 * splits into three simply falls back to marking the whole line, which is where
 * this started. Being wrong is not one of the outcomes.
 */
const ASCII_TERM = /[.!?…]/;
const WIDE_TERM = /[。！？]/;
const CLOSER = /["'”’»)\]]/;

function sentences(text: string): Range[] {
  const out: Range[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const wide = WIDE_TERM.test(ch);
    if (!wide && !ASCII_TERM.test(ch)) continue;
    // Swallow the rest of the run — `?!`, `...` — and any quote or bracket
    // closing after it, so the mark covers `he said."` rather than stopping
    // one character short of the punctuation it belongs to.
    let j = i;
    while (j + 1 < text.length &&
           (ASCII_TERM.test(text[j + 1]) || WIDE_TERM.test(text[j + 1]) || CLOSER.test(text[j + 1]))) j++;
    const next = text[j + 1];
    if (wide || next === undefined || /\s/.test(next)) {
      out.push({ start, end: j + 1 });
      start = j + 1;
      while (start < text.length && /\s/.test(text[start])) start++;
      i = start - 1;
    } else {
      i = j;
    }
  }
  if (start < text.length) out.push({ start, end: text.length });
  return out;
}

export interface Range { start: number; end: number }

/**
 * One piece of the answer and the piece of the source it came from.
 *
 * `out` indexes the reshaped answer; `src` indexes the ORIGINAL string that was
 * passed to `shapeOf` — which is the live textarea value, so the highlight
 * overlay can slice it directly.
 */
export interface Link {
  out: Range;
  src: Range;
  /**
   * One source sentence, one answer sentence — nothing merged, nothing guessed.
   *
   * The highlight does not care: a link that covers two source sentences is a
   * true statement about where that piece of the answer came from, and a
   * coarser mark is exactly what a merged translation deserves. The SENTENCE
   * MEMORY does care, and this is the flag it filters on: filing "these two
   * sentences together" under one key would hand the cache a pair it can never
   * match and, worse, one it could reuse for only half of a later text.
   */
  exact: boolean;
}

export interface Reshaped {
  /** The answer, laid out like the source. */
  text: string;
  /**
   * Answer piece ↔ source piece, or null when the two could not be matched up
   * at all (see the two rules below).
   */
  links: Link[] | null;
}

/**
 * The mark put in front of a line the model is actually being asked for.
 *
 * Must stay identical to `ONLY_MARK` in the backend's lib/modes.ts — that is
 * where the prompt tells the model what it means, and this is where it is
 * written into the text the model reads. See the note there for why the answer
 * is trimmed to the marked lines while the CONTEXT never is.
 */
export const ONLY_MARK = '►';

export interface Unit {
  /** The sentence itself. */
  text: string;
  /** Where it sits in the original string. */
  start: number;
  end: number;
  /** Which segment (line with words on it) it belongs to. */
  line: number;
}

/**
 * The source, split into the pieces a translation can be held in.
 *
 * The same units `reshape` matches the answer against — sentences, grouped by
 * the line they were typed on — but computed from the SOURCE alone, with no
 * answer needed. That is what lets the caller ask "which of these do I already
 * have a translation for?" before deciding what to send.
 *
 * The unit is the sentence rather than the line because that is how people
 * write: a paragraph is typed as one line and grows a sentence at a time. Held
 * by the line, the whole paragraph would go back to the model every time it
 * gained a clause.
 */
export function sourceUnits(shape: TextShape): Unit[] {
  return flatten(shape.segments.map((s) => ({ text: s.text, base: s.start })))
    .map((u) => {
      const seg = shape.segments[u.line];
      return { ...u, text: seg.text.slice(u.start - seg.start, u.end - seg.start) };
    });
}

/**
 * Put an answer back together out of per-unit translations.
 *
 * Produces the canonical shape — one line per source segment, units joined by a
 * space — because that is what `reshape` expects to be handed. The blank lines
 * and indents are applied there, from the source, exactly as they are for an
 * answer that arrived in one piece. Assembling and streaming therefore end up
 * in the same shape, and everything downstream (Copy, Swap, the sentence link,
 * the notes split) needs to know nothing about which path produced the text.
 *
 * A unit with no translation yet is left out rather than filled with the source.
 * A pane showing half a translation and half the original, with no way to tell
 * which is which, is worse than a pane showing only what it actually knows.
 */
export function assemble(shape: TextShape, units: Unit[], parts: (string | null)[]): string {
  const lines: string[] = [];
  shape.segments.forEach((_, line) => {
    const mine = parts.filter((_p, i) => units[i].line === line).filter((p): p is string => !!p);
    lines.push(mine.join(' '));
  });
  // Segments whose every unit is still missing collapse away rather than
  // leaving a blank line the source does not have.
  return lines.filter((l) => l !== '').join('\n');
}

/** Every sentence in the text, in order, tagged with the line it came from. */
function flatten(units: { text: string; base: number }[]): { start: number; end: number; line: number }[] {
  const out: { start: number; end: number; line: number }[] = [];
  units.forEach((u, line) => {
    for (const s of sentences(u.text)) out.push({ start: u.base + s.start, end: u.base + s.end, line });
  });
  return out;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Matching up two texts that do NOT agree on how many pieces they have.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * The two exact rules below (same number of sentences, or same number of
 * lines) are right when they fire and they fire most of the time. When they
 * do not, the pane used to offer NOTHING — and measured against 3,896 real
 * answers out of this product's own database, that was 8% of everything, and
 * 47% of documents over forty lines. One extra line break in a 136-line paste
 * and the whole document went dark. That is the "sometimes it works" the
 * feature was reported for.
 *
 * Nothing is wrong with the answers in those 8%. A model asked for one line
 * per line mostly complies, and where it does not it is for ordinary reasons:
 * it split a long sentence in two, it joined a heading to the line under it,
 * it numbered a list the source numbered differently. The pieces still
 * correspond — just not one for one — and finding that correspondence is a
 * solved problem.
 *
 * ── What it does ────────────────────────────────────────────────────────────
 * The standard length-based sentence alignment (Gale & Church, 1991), which is
 * the algorithm every parallel-corpus tool still uses, with two additions that
 * are free here:
 *
 *   • Translation preserves LENGTH RATIOS. A long sentence translates to a
 *     long sentence, in whatever script — so with one global ratio taken from
 *     the two texts as wholes (`c` below), the expected length of each piece
 *     is known and the deviation from it is the cost. This is what makes it
 *     work across scripts where no word matches anything: Korean runs about
 *     0.5× English by characters, and `c` measures that rather than assuming.
 *   • Translation preserves NUMBERS AND NAMES. "P265", "2026", "GPT-5" and
 *     "Pre-Difficulty" come through untranslated, and a piece that shares one
 *     with a candidate is almost certainly its translation. A small bonus for
 *     shared anchors is what pins numbered lists, which are otherwise the
 *     hardest thing here — every item is the same length as every other.
 *
 * The moves are the classic five: 1–1, 1–2, 2–1, and a piece on either side
 * with no counterpart at all. The penalties are Gale & Church's, which are
 * log-probabilities from a real corpus rather than numbers chosen to make a
 * test pass.
 *
 * ── What it refuses ─────────────────────────────────────────────────────────
 * A mapping that is believed and wrong is worse than none — that principle is
 * older than this function and it still governs. Two guards keep it:
 *
 *   1. The answer must ACCOUNT FOR the source. An answer whose best alignment
 *      consumes less than half of the source is not a rendering of it; it is a
 *      summary, a report, a refusal, or a half-arrived stream. Pointing it at
 *      the first half would be a lie.
 *   2. The alignment must FIT. A cost per piece above `MAX_COST` means the
 *      lengths do not correspond under any arrangement, which is what a
 *      structured report against a flat source looks like.
 *
 * Either way the answer for that pane is the same as before this existed: the
 * text as the model wrote it, and no highlight.
 * ──────────────────────────────────────────────────────────────────────────── */

/** A run of source pieces matched to a run of answer pieces. */
interface Block {
  s0: number; s1: number; a0: number; a1: number;
  /**
   * What this match cost, per answer piece in it.
   *
   * Carried out of the DP so the drawing can say how sure it is. A block that
   * only just won its place gets a WIDER mark rather than a confident one —
   * see `WIDEN_AT`. Undefined on the blocks the hierarchy builds by hand,
   * which are the ones that needed no search.
   */
  cost?: number;
}

/**
 * Gale & Church's own penalties, as −log P(move) from their aligned corpus.
 *
 * Left as they are rather than tuned: they encode how often a human translator
 * actually merges or drops a sentence, and a model asked to translate line by
 * line behaves the same way. Tuning them against the answers in one database
 * would be fitting the noise in that database.
 */
/** Above this many answer lines, `reline` stands aside rather than run its DP. */
const MAX_RELINE_LINES = 400;
/**
 * What it costs to leave a source line with no answer of its own.
 *
 * Small enough that a line the model genuinely skipped still comes out empty,
 * large enough that emptying a line is never merely a cheap way to balance the
 * lengths either side of it.
 */
const EMPTY_GROUP_PENALTY = 4;

const PEN_ONE = 0;
const PEN_MERGE = 3.2;
const PEN_SKIP = 6.8;

/**
 * The variance of the length ratio, from the same paper.
 *
 * Swept across 36 combinations of these three constants against the benchmark
 * in scripts/link-check.ts: every one of them lands between 1.4% and 1.7%
 * wrong. The published values are inside that band, so they stay — the 0.25%
 * that separates the best cell from them is one database's noise, and fitting
 * a prior to it would be the kind of tuning that looks like an improvement
 * and generalises to nothing.
 */
const VAR = 6.8;

/**
 * The worst average fit still worth drawing, per matched piece.
 *
 * A 1–1 match costs the squared deviation from the length the ratio predicts,
 * so 1.0 is one standard deviation out — comfortably normal — and this allows
 * rather more than that because merges carry their penalty on top. Measured on
 * the refusals in this product's database, structured reports against flat
 * sources land far above it and ordinary translations far below; there is a
 * wide empty band in between, which is why the exact value is not delicate.
 */
const MAX_COST = 7.5;

/**
 * The cost above which a match is drawn WIDE instead of precisely.
 *
 * ── Why a third answer between "here" and "nowhere" ────────────────────────
 * The residual errors of any length-based alignment are all the same shape:
 * a run of same-ish short lines with one of them missing, where pairing each
 * piece with its neighbour costs almost exactly what pairing it with its own
 * does. Measured across 36 combinations of the three constants above, that
 * floor does not move — it is not a tuning problem, it is that the text does
 * not say.
 *
 * What it does say is WHICH pairings were close calls, because that is the
 * number the DP just computed. So a block that only just won gets a mark
 * stretched over its neighbours on both sides, and the reader is shown "it is
 * somewhere in here" — which is both true and the thing they wanted to know.
 * A confident block keeps its exact mark.
 *
 * Set at three times a one-standard-deviation match: comfortably above an
 * ordinary 1–1, and below the cost of the coin-flips this exists for.
 */
const WIDEN_AT = 3.0;

/**
 * How much of the source an answer must account for before it may be linked.
 *
 * A quarter, not a half. The guard is here to refuse an answer that is not a
 * rendering of the source at all — a summary, a report, a refusal — and those
 * account for a tenth of it or less. A half turned out to refuse something
 * quite different: the first two lines of a four-line answer, arriving. Every
 * streamed answer passes through that state on its way to being complete, and
 * refusing it means the marks only appear at the end, which is exactly when
 * the reader has stopped needing them.
 */
const MIN_COVERAGE = 0.25;

/** The most DP cells worth filling. Beyond this the text is not a page. */
const MAX_CELLS = 250_000;

/**
 * Text that survives translation, and can therefore be matched on directly.
 *
 * Numbers, and Latin-script runs of four characters or more — names, product
 * codes, identifiers, anything in `code`. Lower-cased so "MAJOR"/"Major" is
 * one anchor. Deliberately not two- or three-letter words: "the" and "and"
 * appear everywhere and would anchor everything to everything.
 */
function anchors(text: string): Set<string> {
  const out = new Set<string>();
  for (const m of text.matchAll(/\d+|[A-Za-z][A-Za-z0-9'-]{3,}/g)) out.add(m[0].toLowerCase());
  return out;
}

/**
 * The punctuation a piece opens and closes with, folded across scripts.
 *
 * Translation changes every word and keeps the punctuation: a line that ended
 * with a colon in English ends with one in Korean, a question stays a
 * question, and a numbered item stays numbered. That makes the two characters
 * at the ends of a piece a genuine signal about which piece it is — and an
 * unusually good one exactly where length fails, because the pieces that
 * length cannot tell apart are short ones, and a short one is mostly its
 * punctuation.
 *
 * The full-width marks fold onto their ASCII twins, or every CJK answer would
 * look like it had no punctuation at all.
 */
const FOLD: Record<string, string> = {
  '。': '.', '．': '.', '！': '!', '？': '?', '：': ':', '；': ';', '，': ',',
  '、': ',', '”': '"', '“': '"', '’': "'", '‘': "'", '）': ')', '（': '(',
};

function endMark(text: string): string {
  const t = text.trimEnd();
  for (let i = t.length - 1; i >= 0 && i > t.length - 4; i--) {
    const ch = FOLD[t[i]] ?? t[i];
    if ('.!?:;'.includes(ch)) return ch;
    if (!'"\')]>…'.includes(ch)) break;
  }
  return '';
}

function startMark(text: string): string {
  const t = text.trimStart();
  if (/^\d/.test(t)) return '#';
  const ch = FOLD[t[0]] ?? t[0] ?? '';
  if ('-•*·—'.includes(ch)) return '-';
  if ('"\'([<'.includes(ch)) return 'q';
  return '';
}

/**
 * Do the two pieces begin and end the same way? A cost reduction, never a
 * penalty.
 *
 * Bonus-only on purpose. A translator who turns a statement into a question,
 * or drops a trailing colon, has done nothing wrong and must not be charged
 * for it; what this rewards is the pair that agrees, which is enough to break
 * the tie between two short neighbours that length alone cannot separate.
 * Measured: it is what fixes "Here the problem is different:" attaching itself
 * to the quoted sentence underneath it.
 */
function shapeBonus(
  a: { head: string; tail: string }, b: { head: string; tail: string },
): number {
  let n = 0;
  if (a.tail && a.tail === b.tail) n += a.tail === '.' ? 0.3 : 1.0;
  if (a.head && a.head === b.head) n += 1.0;
  return n;
}

/**
 * How much two pieces have in common, as a cost REDUCTION.
 *
 * Capped, because this is a hint and not the evidence: three shared numbers do
 * not make a badly-fitting pair good, they make a well-fitting one certain.
 */
function anchorBonus(a: Set<string>, b: Set<string>): number {
  if (!a.size || !b.size) return 0;
  let shared = 0;
  for (const x of a) if (b.has(x)) shared++;
  if (!shared) return 0;
  return Math.min(2.4, shared * 0.9);
}

/**
 * The deviation of one block from the length the global ratio predicts.
 *
 * `(actual − expected) / √expected`, squared: a z-score against a variance
 * that grows with length, which is the whole of Gale & Church's insight. A
 * short piece being twice as long as predicted is normal; a long one being
 * twice as long is not, and the square root is what says so.
 */
function fitCost(srcLen: number, ansLen: number, ratio: number): number {
  const expect = Math.max(ratio * srcLen, 1);
  const d = (ansLen - expect) / Math.sqrt(expect * VAR);
  // Bounded so one wild block cannot dominate a long document's average and
  // sink an alignment that is right everywhere else.
  return Math.min(d * d, 40);
}

/**
 * Align two lists of pieces. Null when they do not correspond.
 *
 * The blocks come back in order and cover every ANSWER piece — an answer piece
 * matched to nothing is returned as a block with an empty source range, and
 * the caller decides what to do with it. Source pieces may be left over at
 * either end; that is what a half-arrived stream looks like, and the coverage
 * guard is what stops it being mistaken for a bad match.
 */
interface Piece {
  start: number;
  end: number;
  text: string;
  line?: number;
  /**
   * The anchors and the end/start punctuation, worked out once.
   *
   * A piece is aligned more than once — the hierarchy runs the same DP over
   * the lines and then again over the sentences inside each block — and these
   * were being re-derived by regular expression every time, for every piece,
   * on every streamed chunk. Cached on the piece itself, which is the object
   * that outlives the call.
   */
  mark?: { anchors: Set<string>; head: string; tail: string };
}

function marksOf(p: Piece): { anchors: Set<string>; head: string; tail: string } {
  if (!p.mark) p.mark = { anchors: anchors(p.text), head: startMark(p.text), tail: endMark(p.text) };
  return p.mark;
}

function align(src: Piece[], ans: Piece[], given?: number): Block[] | null {
  const n = src.length;
  const m = ans.length;
  if (!n || !m) return null;
  if (n * m > MAX_CELLS) return null;

  const sLen = src.map((u) => u.text.length);
  const aLen = ans.map((u) => u.text.length);
  const totalS = sLen.reduce((x, y) => x + y, 0);
  const totalA = aLen.reduce((x, y) => x + y, 0);
  if (!totalS || !totalA) return null;
  /*
   * The ratio is measured over the WHOLE of both texts and handed down.
   *
   * Two sentences are not enough to estimate it from — a block of one short
   * line would decide that this language is 30% longer than that one and
   * judge its own pieces against a number it invented. The document knows
   * better than the paragraph, and the paragraph better than the line.
   */
  const ratio = given ?? totalA / totalS;

  const sMark = src.map(marksOf);
  const aMark = ans.map(marksOf);
  const sAnchor = sMark.map((x) => x.anchors);
  const aAnchor = aMark.map((x) => x.anchors);

  const INF = Infinity;

  /*
   * Prefix sums, and anchor sets built once per piece.
   *
   * This loop is the hot one: it runs on every streamed chunk of every answer
   * whose structure did not match, which for a long document is several times
   * a second. Written the obvious way — a `Set` built per transition to union
   * two pieces' anchors — a 87-line document measured 235ms per chunk, which
   * is four dropped frames each time a few words arrive. Nothing here is
   * clever; it just refuses to allocate inside the loop.
   */
  const sPre = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) sPre[i + 1] = sPre[i] + sLen[i];
  const aPre = new Float64Array(m + 1);
  for (let j = 0; j < m; j++) aPre[j + 1] = aPre[j] + aLen[j];

  /** Shared anchors between one source piece and one answer piece. */
  const shared = (i: number, j: number): number => {
    const a = sAnchor[i];
    const b = aAnchor[j];
    if (!a.size || !b.size) return 0;
    let k = 0;
    // Walk the smaller of the two — `has` is the constant-time half.
    if (a.size <= b.size) { for (const t of a) if (b.has(t)) k++; }
    else { for (const t of b) if (a.has(t)) k++; }
    return k;
  };

  const dp: Float64Array[] = [];
  const from: Int8Array[] = [];
  for (let i = 0; i <= n; i++) {
    dp.push(new Float64Array(m + 1).fill(INF));
    from.push(new Int8Array(m + 1).fill(-1));
  }
  dp[0][0] = 0;

  /*
   * The five moves, hand-written rather than looped over a table.
   *
   * Each one is (source pieces, answer pieces) with its penalty: 1–1, then the
   * merge and the split, then a piece on either side with no counterpart. The
   * anchor bonus for a merge is the sum over its pieces rather than a union,
   * which is the same number until the same anchor appears twice — and the cap
   * makes that difference nothing.
   */
  for (let i = 0; i <= n; i++) {
    const row = dp[i];
    for (let j = 0; j <= m; j++) {
      const here = row[j];
      if (here === INF) continue;

      // 1–1
      if (i < n && j < m) {
        const c = here + fitCost(sLen[i], aLen[j], ratio) + PEN_ONE
          - Math.min(2.4, shared(i, j) * 0.9)
          - shapeBonus(sMark[i], aMark[j]);
        if (c < dp[i + 1][j + 1]) { dp[i + 1][j + 1] = c; from[i + 1][j + 1] = 0; }
      }
      // 1–2: the model split one sentence in two.
      if (i < n && j + 1 < m) {
        const c = here + fitCost(sLen[i], aLen[j] + aLen[j + 1], ratio) + PEN_MERGE
          - Math.min(2.4, (shared(i, j) + shared(i, j + 1)) * 0.9);
        if (c < dp[i + 1][j + 2]) { dp[i + 1][j + 2] = c; from[i + 1][j + 2] = 1; }
      }
      // 2–1: the model ran two sentences together.
      if (i + 1 < n && j < m) {
        const c = here + fitCost(sLen[i] + sLen[i + 1], aLen[j], ratio) + PEN_MERGE
          - Math.min(2.4, (shared(i, j) + shared(i + 1, j)) * 0.9);
        if (c < dp[i + 2][j + 1]) { dp[i + 2][j + 1] = c; from[i + 2][j + 1] = 2; }
      }
      // 1–0: a source sentence with nothing answering it.
      if (i < n) {
        const c = here + PEN_SKIP;
        if (c < dp[i + 1][j]) { dp[i + 1][j] = c; from[i + 1][j] = 3; }
      }
      // 0–1: an answer sentence the source never had.
      if (j < m) {
        const c = here + PEN_SKIP;
        if (c < dp[i][j + 1]) { dp[i][j + 1] = c; from[i][j + 1] = 4; }
      }
    }
  }

  /** Matching `from`, in the same order. */
  const MOVES: [number, number][] = [[1, 1], [1, 2], [2, 1], [1, 0], [0, 1]];

  /*
   * Every answer piece must be consumed; the source may be left hanging.
   *
   * That asymmetry is deliberate and it is what makes this work WHILE THE
   * ANSWER IS STILL ARRIVING. A stream is a correct translation of the first
   * part of the source and nothing at all of the rest, so charging it for the
   * part that has not been written yet would refuse every answer until its
   * last chunk — which is precisely when the reader has stopped needing to be
   * told where they are. The coverage guard below is what keeps this honest.
   */
  /*
   * ⚠️ AND A SHORT TAIL IS NOT FREE, which is the whole difficulty.
   *
   * Left entirely free, the cheapest way to answer a source with one sentence
   * missing from the middle is to shift every pairing after the gap by one and
   * let the LAST source sentence fall off the end — which costs nothing at
   * all, while the truth costs one skip. Measured: that single rule was 4% of
   * all pieces, quietly pointing each one at its neighbour.
   *
   * The two cases are told apart by how much is left over, because that is
   * genuinely what distinguishes them. An answer still being written stops
   * somewhere in the middle and leaves a LOT of source behind; a complete
   * answer that has quietly shed its last line leaves one sentence. So a short
   * tail is charged as the skip it is, and a long one is free — and the
   * decision between the shifted reading and the true one then comes down to
   * which one fits the lengths, which is the question that should decide it.
   */
  let end = -1;
  let best = INF;
  for (let i = 0; i <= n; i++) {
    if (dp[i][m] === INF) continue;
    const left = sPre[n] - sPre[i];
    const charge = left > 0 && left < totalS * 0.2 ? PEN_SKIP * (n - i) : 0;
    if (dp[i][m] + charge < best) { best = dp[i][m] + charge; end = i; }
  }
  if (end < 0 || best === INF) return null;

  const consumed = sPre[end];
  if (consumed < totalS * MIN_COVERAGE) return null;

  // Walk the path back out.
  const blocks: Block[] = [];
  let i = end;
  let j = m;
  while (i > 0 || j > 0) {
    const move = from[i][j];
    if (move < 0) return null;
    const [di, dj] = MOVES[move];
    // What this step cost on its own: the difference between the total here
    // and the total at the cell it came from.
    const step = dp[i][j] - dp[i - di][j - dj];
    blocks.push({ s0: i - di, s1: i, a0: j - dj, a1: j, cost: step / Math.max(dj, 1) });
    i -= di;
    j -= dj;
  }
  blocks.reverse();

  /*
   * The unconsumed source tail is recorded as a block of its own.
   *
   * It links nothing — there are no answer pieces in it — and it exists only
   * so that `linksFrom` can see that the last piece of the answer has
   * something after it that was not translated. That is true both of a
   * streaming answer (the rest is still being written) and of one that ended
   * early, and in both cases the final mark is a guess about where the
   * answer stopped rather than a fact.
   */
  if (end < n) blocks.push({ s0: end, s1: n, a0: m, a1: m });

  /*
   * The fit, per piece that actually had to be judged.
   *
   * Averaged over the MATCHED blocks rather than over all of them: a long
   * answer that skips two source lines is not twice as suspect as one that
   * skips one, and dividing by a count that includes the skips would let a
   * pile of cheap skips hide one impossible match.
   */
  const matched = blocks.filter((b) => b.s1 > b.s0 && b.a1 > b.a0).length;
  if (!matched) return null;
  if (best / matched > MAX_COST) return null;

  return blocks;
}

/**
 * Align two texts BY THEIR LINES FIRST, then sentence by sentence inside each
 * matched run of lines.
 *
 * ── Why the hierarchy is not an optimisation ────────────────────────────────
 * Length is weak evidence between two adjacent sentences of similar length:
 * pairing each with its neighbour's translation costs almost the same as
 * pairing it with its own, so one dropped sentence can slide an entire
 * document by one and the arithmetic will not notice. Measured flat, that
 * drift was 10% of pieces on answers with a sentence missing.
 *
 * Lines are longer, so their lengths separate; and matching them first PENS
 * the sentence alignment inside a handful of lines, where a mistake can no
 * longer propagate past the end of its own paragraph. The same DP does both
 * jobs — only the pieces it is given change.
 *
 * Falls back to one flat sentence alignment when the lines themselves do not
 * correspond, which is the real case of a paragraph answered as five lines.
 */
function alignHierarchical(
  srcLines: Piece[], srcUnits: (Piece & { line: number })[],
  ansLines: Piece[], ansUnits: (Piece & { line: number })[],
): Block[] | null {
  const totalS = srcUnits.reduce((n, u) => n + u.text.length, 0);
  const totalA = ansUnits.reduce((n, u) => n + u.text.length, 0);
  if (!totalS || !totalA) return null;
  const ratio = totalA / totalS;

  const flat = () => align(srcUnits, ansUnits, ratio);

  // One line on either side has no structure to exploit.
  if (srcLines.length < 2 || ansLines.length < 2) return flat();

  const lineBlocks = align(srcLines, ansLines, ratio);
  if (!lineBlocks) return flat();

  const out: Block[] = [];
  for (const lb of lineBlocks) {
    const mineSrc: number[] = [];
    const mineAns: number[] = [];
    srcUnits.forEach((u, i) => { if (u.line >= lb.s0 && u.line < lb.s1) mineSrc.push(i); });
    ansUnits.forEach((u, j) => { if (u.line >= lb.a0 && u.line < lb.a1) mineAns.push(j); });

    /*
     * ⚠️ A SOURCE LINE THE ANSWER DOES NOT HAVE IS KEPT, as a block with no
     * answer pieces in it — the same rule as the inner loop below, and the
     * same reason.
     *
     * It links nothing, so dropping it looked free. It is not: `linksFrom`
     * reads these gaps to decide which marks are uncertain, and a line the
     * model did not translate is the single biggest reason the mark beside it
     * might belong to its neighbour instead. Measured in a browser on a
     * four-line source answered in three: both neighbours were drawn as
     * confident exact matches, and one of them was the wrong line.
     */
    if (!mineAns.length) {
      if (mineSrc.length) {
        const at = out.length ? out[out.length - 1].a1 : 0;
        out.push({ s0: mineSrc[0], s1: mineSrc[mineSrc.length - 1] + 1, a0: at, a1: at });
      }
      continue;
    }

    /*
     * A run of answer lines with no source lines at all — a heading the model
     * added. Emitted as a block with an empty source range, which is what
     * `linksFrom` reads as "this piece has no counterpart".
     */
    if (!mineSrc.length) {
      out.push({ s0: lb.s0, s1: lb.s0, a0: mineAns[0], a1: mineAns[mineAns.length - 1] + 1 });
      continue;
    }

    // One line each with the same number of sentences is the ordinary case and
    // needs no search: they correspond in order.
    if (lb.s1 - lb.s0 === 1 && lb.a1 - lb.a0 === 1 && mineSrc.length === mineAns.length) {
      mineSrc.forEach((si, k) => {
        out.push({ s0: si, s1: si + 1, a0: mineAns[k], a1: mineAns[k] + 1 });
      });
      continue;
    }

    const inner = align(
      mineSrc.map((i) => srcUnits[i]),
      mineAns.map((j) => ansUnits[j]),
      ratio,
    );
    if (inner) {
      for (const b of inner) {
        const hasSrc = b.s1 > b.s0;
        const hasAns = b.a1 > b.a0;
        /*
         * ⚠️ A SOURCE SENTENCE THE MODEL LEFT OUT IS KEPT, as a block with no
         * answer pieces in it.
         *
         * It links nothing — there is nothing to point at it — and the first
         * version of this dropped it for exactly that reason. That was wrong
         * in a way that took a browser to see: `linksFrom` reads the gaps
         * BETWEEN blocks to decide which marks are uncertain, and a dropped
         * sentence is the single biggest reason a mark beside it might have
         * slid. Removing the block removed the evidence, and the two
         * neighbours were then drawn as confident exact matches.
         */
        const aPos = hasAns
          ? mineAns[b.a0]
          : (b.a0 < mineAns.length ? mineAns[b.a0] : mineAns[mineAns.length - 1] + 1);
        out.push({
          // `0,0` is the empty source range `linksFrom` reads as "this piece
          // has no counterpart" — never an index, which would name a sentence.
          s0: hasSrc ? mineSrc[b.s0] : 0,
          s1: hasSrc ? mineSrc[b.s1 - 1] + 1 : 0,
          a0: aPos,
          a1: hasAns ? mineAns[b.a1 - 1] + 1 : aPos,
        });
      }
      continue;
    }

    /*
     * The sentences inside this block do not correspond, but the LINES do.
     *
     * So the block is marked as one piece: every answer sentence in it points
     * at the whole run of source lines it came from. Coarser than a sentence
     * and completely true, which is the trade this file makes everywhere.
     */
    out.push({
      s0: mineSrc[0], s1: mineSrc[mineSrc.length - 1] + 1,
      a0: mineAns[0], a1: mineAns[mineAns.length - 1] + 1,
    });
  }

  return out.length ? out : null;
}

/**
 * Does the text itself say the one-for-one reading is wrong?
 *
 * The two exact rules take equal counts as proof of correspondence, and almost
 * always it is. It is not proof when a model MERGES one pair of sentences and
 * SPLITS another: the count comes out the same and everything between the two
 * is off by one, which is the worst outcome this file has — a confident
 * mapping, uniformly wrong, through the middle of a document.
 *
 * Counting alone cannot see it. Anchors can: a piece carrying "P265" whose
 * supposed source carries "P264" instead, while the NEXT source piece carries
 * "P265", is not a translation of what it has been paired with. That is hard
 * evidence rather than a length guess, so one contradiction is enough to hand
 * the text to the aligner — which is free to merge and split, and will.
 *
 * Silent when there is nothing to go on: a text with no numbers, names or code
 * in it produces no anchors, no contradictions, and keeps the one-for-one
 * reading, which remains the right default.
 */
function contradicts(src: Piece[], ans: Piece[]): boolean {
  if (src.length !== ans.length || src.length < 3) return false;
  const sa = src.map((u) => anchors(u.text));
  const aa = ans.map((u) => anchors(u.text));
  const shares = (i: number, j: number) => {
    if (i < 0 || i >= sa.length) return false;
    for (const t of aa[j]) if (sa[i].has(t)) return true;
    return false;
  };
  for (let j = 0; j < ans.length; j++) {
    if (!aa[j].size || shares(j, j)) continue;
    // It matched somebody else and not its own partner.
    if (shares(j - 1, j) || shares(j + 1, j)) return true;
  }
  return false;
}

/**
 * Turn an alignment into links into the answer.
 *
 * An answer piece that matched nothing takes the source range of the piece
 * beside it — the previous one where there is one, the next otherwise. That is
 * a decision worth stating: the alternative is a sentence that lights nothing
 * up when it is pointed at, and a highlight that does nothing is exactly what
 * a broken feature looks like from the reader's chair. Borrowing the
 * neighbour's range says "this belongs with that part of your text", which is
 * both true and useful, and `exact: false` keeps it out of the memory.
 */
function linksFrom(blocks: Block[], src: Piece[], ans: Piece[]): Link[] {
  const links: Link[] = [];
  /** Where each answer piece's block pointed, or null for an orphan. */
  const ranges: (Range | null)[] = new Array(ans.length).fill(null);
  const exact: boolean[] = new Array(ans.length).fill(false);

  /**
   * Is this block sitting next to a seam?
   *
   * A seam is anywhere the two texts stopped corresponding one for one — a
   * merge, a split, or a piece with no counterpart. Everything else in the
   * alignment is a 1–1 match that the DP chose against the two pieces beside
   * it, and next to a seam those two pieces are exactly what has moved.
   *
   * This is the rule that catches the residual error, and the reason a cost
   * threshold alone does not: the drifted pairing is not an expensive match.
   * It is a cheap one that happens to be wrong, because the sentence it
   * should have taken was dropped and its neighbour is the same length. The
   * cost cannot see that. The seam can.
   */
  const seam = (k: number) => {
    const b = blocks[k];
    if (!b) return false;
    return b.s1 - b.s0 !== 1 || b.a1 - b.a0 !== 1;
  };

  blocks.forEach((b, k) => {
    if (b.a1 <= b.a0) return;
    let s0 = b.s0;
    let s1 = b.s1;
    /*
     * An uncertain block is drawn WIDE rather than precisely — one source
     * sentence further — and widened TOWARDS the seam and no further.
     *
     * The uncertainty has a direction: a sentence went missing on one
     * particular side, and that is the side this piece may have slid from.
     * The reader is shown "somewhere in here", which is what the alignment
     * actually knows; `exact` is false so nothing downstream mistakes it for
     * a pair; and a block sitting between two ordinary 1–1 matches is not in
     * doubt at all and keeps its exact mark, which is what stops this from
     * quietly coarsening every mark in the document.
     *
     * Measured against the benchmark in scripts/link-check.ts: it takes the
     * pieces that point at the WRONG sentence from 1.65% to 1.20%, and costs
     * about five in a hundred their precise mark. Both directions at once
     * reached 1.04% and cost twelve in a hundred, which is the wrong trade —
     * a mark that covers three sentences is a mark somebody has to read.
     */
    const before = seam(k - 1);
    const after = seam(k + 1);
    const unsure = (b.cost ?? 0) > WIDEN_AT || before || after;
    /*
     * …but never on a text too short to be vague about.
     *
     * Widening says "somewhere in here". On a two-sentence source, "here" is
     * the whole thing, and a mark that covers everything answers nothing —
     * the reader is better served by the precise guess, which on a text that
     * short they can check at a glance anyway.
     */
    const roomToBeVague = src.length >= 4;
    if (s1 > s0 && roomToBeVague) {
      if (before || (b.cost ?? 0) > WIDEN_AT) s0 = Math.max(0, s0 - 1);
      if (after || (b.cost ?? 0) > WIDEN_AT) s1 = Math.min(src.length, s1 + 1);
    }
    const range = s1 > s0 ? { start: src[s0].start, end: src[s1 - 1].end } : null;
    for (let j = b.a0; j < b.a1; j++) {
      ranges[j] = range;
      // `exact` still says what the ALIGNMENT knows, not what was drawn: a
      // block left precise only because the text was too short to widen is
      // no more certain than one that was widened, and the memory must not
      // take it for a pair.
      exact[j] = !!range && !unsure && b.s1 - b.s0 === 1 && b.a1 - b.a0 === 1;
    }
  });

  // Orphans inherit, forwards then backwards, so a piece at the very start is
  // covered by the one after it.
  let last: Range | null = null;
  for (let j = 0; j < ranges.length; j++) {
    if (ranges[j]) last = ranges[j];
    else ranges[j] = last;
  }
  let next: Range | null = null;
  for (let j = ranges.length - 1; j >= 0; j--) {
    if (ranges[j]) next = ranges[j];
    else ranges[j] = next;
  }

  ans.forEach((u, j) => {
    const r = ranges[j];
    if (!r) return;
    links.push({ out: { start: u.start, end: u.end }, src: r, exact: exact[j] });
  });

  return links.length ? links : [];
}

/**
 * Put the source's layout back onto the answer, and match the two up.
 *
 * TWO JOBS, AND THEY ARE NOT THE SAME JOB — which is the thing to understand
 * before changing anything here:
 *
 *   • **the layout**: whether the answer is re-laid out under the source's own
 *     blank lines and indents, or left exactly as the model wrote it;
 *   • **the links**: which piece of the answer came from which piece of the
 *     source.
 *
 * They used to be decided together, and that is what broke the links. Rebuilding
 * the layout is only safe when the two texts correspond piece for piece, so the
 * rules below are strict about it — and the links were thrown away with it
 * whenever they were not met, which measured on this product's own database was
 * 8% of all answers and 47% of documents over forty lines.
 *
 * So the layout keeps its strict rules, and the links get their own weaker one:
 *
 *   1. **Same number of SENTENCES** — they correspond in order, whatever either
 *      side did with its line breaks. The layout is rebuilt from the source, so
 *      an answer that arrived as three lines becomes the one paragraph that was
 *      typed, and vice versa. Links are one for one and `exact`.
 *   2. **Same number of LINES** — the fallback for text with no sentence
 *      punctuation to count: a shopping list, a line of code, fragments. The
 *      layout is still restored. Links are per line, except where a line pair
 *      holds the same number of sentences on both sides, which is refined down
 *      to the sentence — a coarser mark than the reader could have had is its
 *      own small failure.
 *   3. **Neither** — the answer is left EXACTLY as the model wrote it, because
 *      an answer that merged two paragraphs or grew a heading of its own has a
 *      shape this function has no business overriding. The links are then found
 *      by `align` above, which is allowed to merge and to skip, and which
 *      refuses outright rather than guess. See its own notes.
 *
 * This runs on every streamed chunk, so a half-arrived answer is the normal
 * case: fewer sentences and fewer lines than the source, falling to rule 3,
 * where the aligner matches what has arrived and leaves the rest of the source
 * hanging — so the marks now appear while the answer is still being written
 * rather than only at the end.
 */
/**
 * Fold an answer that re-flowed its lines back onto the source's own lines.
 *
 * `reshape` restores the writer's layout from two rules, and both of them are
 * counting rules: same number of sentences, or same number of lines. An answer
 * that satisfies neither keeps its own layout, which is the honest thing to do
 * when nothing is known about how the two correspond — a paragraph would
 * otherwise be invented around whatever the model happened to emit.
 *
 * Translate's EASY tab is the case where something IS known, and where falling
 * through hurts most. That tab is asked to break long sentences up, so its
 * sentence count is different on purpose; and having split one, the model
 * usually puts the pieces on their own lines as well. Both counting rules miss,
 * and the reader — who is looking at their own three-paragraph message beside
 * an answer run together into one block, with the blank lines gone — sees the
 * tab as the one that "does not show the paragraphs". The faithful tab has no
 * such problem: it is assembled a sentence at a time by the incremental
 * machinery, so its correspondence is built rather than inferred.
 *
 * So this is the missing step and not a fourth rule inside `reshape`: the
 * aligner already works out which ANSWER lines belong to which SOURCE line, and
 * that is exactly the fact needed to put the answer back on the source's lines.
 * Joining each group with a space returns an answer with one line per segment,
 * which `reshape` then lays out under the line rule as usual — so the gaps, the
 * indents and the per-line marks all come back, and none of `reshape`'s own
 * measured behaviour changes for anything that was already working.
 *
 * Returns the answer untouched whenever there is nothing to gain or nothing to
 * go on: counts already equal, one side with no structure, or an aligner that
 * refuses. Never invents a line the answer did not have — a segment with no
 * answer lines of its own comes back empty, which is what a line the model
 * declined to translate looks like everywhere else in this file.
 */
export function reline(answer: string, shape: TextShape): string {
  const segs = shape.segments;
  const clean = answer.replace(/\r\n?/g, '\n');
  const lines = clean.split('\n').map((l) => l.trim()).filter(Boolean);
  // One source line: there is only one group to fold into, so the fold is a
  // plain join. The Easy tab hits this most — a one-line message split into
  // short sentences came back as a column of them.
  if (segs.length === 1) return lines.length > 1 ? lines.join(' ') : answer;
  if (segs.length < 2) return answer;
  // Already one line per segment: the line rule will take it as it stands, and
  // re-joining could only make it worse.
  if (lines.length <= segs.length) return answer;
  // Both bounds are guards on the loop below, not judgements about the text.
  if (lines.length > MAX_RELINE_LINES || segs.length * lines.length * lines.length > MAX_CELLS) {
    return answer;
  }

  const srcLen = segs.map((g) => g.text.trim().length);
  const ansLen = lines.map((l) => l.length);
  const totalS = srcLen.reduce((a, b) => a + b, 0);
  const totalA = ansLen.reduce((a, b) => a + b, 0);
  if (!totalS || !totalA) return answer;

  // How much longer the answer runs than the source, overall. Every group is
  // judged against its own segment scaled by this, so a language that simply
  // takes more characters to say the same thing costs nothing.
  const ratio = totalA / totalS;

  // Prefix sums, so the length of any run of answer lines is one subtraction.
  const pre = [0];
  for (const n of ansLen) pre.push(pre[pre.length - 1] + n);

  /*
   * What it costs to give answer lines [j0, j1) to source segment i.
   *
   * Squared error against the length this segment was expected to produce,
   * normalised by that length so a long paragraph is not automatically the
   * expensive one. The empty-group penalty is what stops the DP from parking
   * every line on one segment and leaving the rest blank: a source line that
   * produced nothing is possible — the model can decline a line — but it is
   * unusual enough to need a reason.
   */
  const cost = (i: number, j0: number, j1: number): number => {
    const want = ratio * srcLen[i] + 1;
    const got = pre[j1] - pre[j0];
    const d = got - want;
    return (d * d) / want + (j1 === j0 ? EMPTY_GROUP_PENALTY : 0);
  };

  /*
   * The partition itself: split the answer's lines into exactly one contiguous
   * run per source segment, in order, at the least total cost.
   *
   * A plain monotonic segmentation, deliberately NOT `align` above. That is
   * Gale & Church, whose move set is 1:1, 1:2, 2:1 and the two skips — it has
   * no way to say that ONE source sentence became THREE, which is precisely
   * what this tab does whenever it breaks a long sentence up. Handed a
   * three-way split it has to spend its expansions somewhere else, and what it
   * actually did, measured on a three-paragraph message, was hand the second
   * paragraph's opening sentence to the end of the first. Runs of any length
   * are the whole point here, so the problem gets its own small DP rather than
   * the shared aligner bent towards it.
   */
  const INF = Infinity;
  // best[i][j] — first i segments filled from the first j answer lines.
  const best: number[][] = Array.from({ length: segs.length + 1 }, () => new Array<number>(lines.length + 1).fill(INF));
  const from: number[][] = Array.from({ length: segs.length + 1 }, () => new Array<number>(lines.length + 1).fill(0));
  best[0][0] = 0;
  for (let i = 1; i <= segs.length; i += 1) {
    for (let j = 0; j <= lines.length; j += 1) {
      for (let k = 0; k <= j; k += 1) {
        const prev = best[i - 1][k];
        if (prev === INF) continue;
        const c = prev + cost(i - 1, k, j);
        if (c < best[i][j]) { best[i][j] = c; from[i][j] = k; }
      }
    }
  }
  if (best[segs.length][lines.length] === INF) return answer;

  // Walk the choices back out. Every answer line lands in exactly one group by
  // construction, so nothing can be dropped or repeated here.
  const out = new Array<string>(segs.length).fill('');
  let j = lines.length;
  for (let i = segs.length; i > 0; i -= 1) {
    const k = from[i][j];
    out[i - 1] = lines.slice(k, j).join(' ');
    j = k;
  }
  return out.some(Boolean) ? out.join('\n') : answer;
}

/** Extras for callers that know something `reshape` cannot work out for itself. */
export interface ReshapeOptions {
  /**
   * The answer's lines already correspond to the shape's segments one for one,
   * because the caller put them that way — see `reline`.
   *
   * It switches off the `contradicts` veto on the LINE RULE, and only there.
   * That veto reads "this line carries a word that belongs to its NEIGHBOUR's
   * source line, and none of its own" as evidence the two texts are paired off
   * by one, which is the right reading of an ordinary translation and the wrong
   * one for Translate's Easy tab: that tab is asked to make implied things
   * explicit, so "- roll it back" legitimately becomes "roll back that commit"
   * and picks up the anchor of the line above it. Measured on a six-line
   * message, one such line was enough to veto the whole layout and drop the
   * reader back to an answer with no paragraphs in it.
   *
   * Only the veto is skipped. The marks the line rule then draws are the
   * conservative whole-line ones wherever the two sides count their sentences
   * differently, which is the honest answer for a line that was rewritten.
   */
  trustLines?: boolean;
}

export function reshape(answer: string, shape: TextShape, opts?: ReshapeOptions): Reshaped {
  if (!shape.segments.length) return { text: answer, links: null };

  // The answer's own lines are only a starting point. What the model was ASKED
  // for is one line per source line, and it mostly complies — but "mostly" was
  // never good enough to build on, and the failure is not random: a single
  // paragraph containing three sentences comes back as three lines about as
  // often as it comes back as one. Both are the same answer.
  /*
   * One normalised copy of the answer, made once.
   *
   * `\r\n` from a Windows-ish model answer would otherwise put every offset
   * downstream one character out per line — and these offsets are what the
   * highlight slices with, so being out by a few characters is a mark that
   * starts mid-word.
   */
  const clean = answer.replace(/\r\n?/g, '\n');
  const lines = clean.split('\n');
  const ansLines: { text: string; base: number }[] = [];
  let at = 0;
  for (const line of lines) {
    const body = line.trim();
    if (body) ansLines.push({ text: body, base: at + (line.length - line.trimStart().length) });
    at += line.length + 1;
  }
  if (!ansLines.length) return { text: answer, links: null };

  const srcUnits = flatten(shape.segments.map((s) => ({ text: s.text, base: s.start })));
  const ansUnits = flatten(ansLines);

  /*
   * Every piece with its own text attached, built once.
   *
   * The two sides are slices of two DIFFERENT strings and that is deliberate:
   * source offsets index the original box value, which is never normalised
   * (see `shapeOf`), while answer offsets index `clean`, which is what this
   * function returns and therefore what the answer pane slices.
   */
  const srcPieces: Piece[] = srcUnits.map((u) => {
    const seg = shape.segments[u.line];
    return {
      start: u.start,
      end: u.end,
      line: u.line,
      text: seg.text.slice(u.start - seg.start, u.end - seg.start),
    };
  });
  const ansPieces: Piece[] = ansUnits.map((u) => ({
    start: u.start, end: u.end, line: u.line, text: clean.slice(u.start, u.end),
  }));

  /*
   * Equal counts are evidence, not proof — see `contradicts`. When the text
   * itself says the one-for-one reading is wrong, both exact rules stand
   * aside and the aligner takes it.
   */
  const oneForOne = srcUnits.length === ansUnits.length && !contradicts(srcPieces, ansPieces);

  // ── The sentence rule ─────────────────────────────────────────────────────
  //
  // Sentences, not lines, are what the two texts actually have in common. A
  // line break is a layout choice — the writer's, and then the model's — while
  // a sentence is a unit of meaning that survives translation. So when both
  // sides contain the same NUMBER of sentences, they correspond in order, and
  // it does not matter at all how either side chose to break its lines.
  //
  // This is what makes the layout come out right in the case the line rule
  // could not handle: one typed paragraph of three sentences, answered as three
  // lines. Regrouped under the source's own layout it becomes one paragraph
  // again, which is what was written.
  if (oneForOne) {
    let out = '';
    const links: Link[] = [];
    let k = 0;

    shape.segments.forEach((_, i) => {
      out += shape.gaps[i] + shape.indents[i];
      const mine = srcUnits.filter((u) => u.line === i);
      mine.forEach((src, n) => {
        if (n > 0) out += ' ';
        const ans = ansUnits[k++];
        const text = clean.slice(ans.start, ans.end);
        links.push({
          out: { start: out.length, end: out.length + text.length },
          src: { start: src.start, end: src.end },
          exact: true,
        });
        out += text;
      });
    });
    out += shape.tail;
    return { text: out, links };
  }

  // ── The line rule ─────────────────────────────────────────────────────────
  //
  // The fallback for text the sentence splitter cannot read the same way on
  // both sides — a list of fragments with no full stops, a line of code, a
  // language whose punctuation this file does not know. Line for line still
  // restores the layout; the marks are as fine as each pair of lines allows.
  if (ansLines.length === shape.segments.length && (opts?.trustLines || !contradicts(
    shape.segments.map((g) => ({ start: g.start, end: g.end, text: g.text })),
    ansLines.map((l) => ({ start: l.base, end: l.base + l.text.length, text: l.text })),
  ))) {
    let out = '';
    const links: Link[] = [];
    ansLines.forEach((line, i) => {
      out += shape.gaps[i] + shape.indents[i];
      const src = shape.segments[i];
      const mineSrc = srcUnits.filter((u) => u.line === i);
      const mineAns = ansUnits.filter((u) => u.line === i);
      /*
       * Refined to the sentence where both sides of THIS line have the same
       * number of them, and left as the whole line where they do not.
       *
       * The decision is taken per line rather than for the text as a whole,
       * which is the point: one paragraph whose sentences the model counted
       * differently used to coarsen every other line in the document to a
       * whole-line mark. Now it coarsens itself and nothing else.
       */
      if (mineSrc.length > 1 && mineSrc.length === mineAns.length) {
        mineAns.forEach((a, k) => {
          links.push({
            out: {
              start: out.length + (a.start - line.base),
              end: out.length + (a.end - line.base),
            },
            src: { start: mineSrc[k].start, end: mineSrc[k].end },
            exact: true,
          });
        });
      } else {
        links.push({
          out: { start: out.length, end: out.length + line.text.length },
          src: { start: src.start, end: src.end },
          exact: mineSrc.length === 1 && mineAns.length === 1,
        });
      }
      out += line.text;
    });
    out += shape.tail;
    return { text: out, links };
  }

  /*
   * ── Neither rule fits ─────────────────────────────────────────────────────
   *
   * The answer keeps its own layout — see the note at the top of this function
   * for why re-laying it out here would be overreach — and the links are found
   * rather than counted out. `align` refuses on its own terms, so `links: null`
   * still happens; it is now the answer to "these two texts do not correspond"
   * instead of to "these two texts disagree about line breaks".
   */
  const blocks = alignHierarchical(
    shape.segments.map((g) => ({ start: g.start, end: g.end, text: g.text })),
    srcPieces as (Piece & { line: number })[],
    ansLines.map((l) => ({ start: l.base, end: l.base + l.text.length, text: l.text })),
    ansPieces as (Piece & { line: number })[],
  );
  if (!blocks) return { text: clean, links: null };

  return { text: clean, links: linksFrom(blocks, srcPieces, ansPieces) };
}
